import { apiFetch } from '../utils/apiFetch';
import { API_CONFIG } from '../constants';
import type {
  CreateGuestRequest,
  GuestResponse,
  StartGuestSessionRequest,
  StartGuestSessionResponse,
} from '../dtos/guest';

export class GuestApiError extends Error {
  status: number;
  errorCode?: string;
  payload?: unknown;

  constructor(message: string, status: number, errorCode?: string, payload?: unknown) {
    super(message);
    this.name = 'GuestApiError';
    this.status = status;
    this.errorCode = errorCode;
    this.payload = payload;
  }
}

async function throwGuestApiError(response: Response, action: string): Promise<never> {
  const raw = await response.text().catch(() => '');
  let payload: unknown = raw;
  let errorCode: string | undefined;

  if (raw) {
    try {
      const parsed = JSON.parse(raw) as { errorCode?: string };
      payload = parsed;
      errorCode = parsed.errorCode;
    } catch {
      // Keep raw text payload for non-JSON responses.
    }
  }

  throw new GuestApiError(
    `${action} failed (${response.status})${errorCode ? ` [${errorCode}]` : ''}`,
    response.status,
    errorCode,
    payload
  );
}

export async function createGuest(guest: CreateGuestRequest): Promise<GuestResponse> {
  const response = await apiFetch({
    url: API_CONFIG.ENDPOINTS.CREATE_GUEST,
    method: 'POST',
    body: guest,
  });

  if (!response.ok) {
    await throwGuestApiError(response, 'Create guest');
  }

  return response.json();
}

export async function startGuestSession(
  session: StartGuestSessionRequest
): Promise<StartGuestSessionResponse> {
  const response = await apiFetch({
    url: API_CONFIG.ENDPOINTS.START_GUEST_SESSION,
    method: 'POST',
    body: session,
  });

  if (!response.ok) {
    await throwGuestApiError(response, 'Start guest session');
  }

  return response.json();
}
